'use client'

import { Description, Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react'
import { ExclamationCircleIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { noop } from 'lodash'
import type { ReactNode } from 'react'

import type { ButtonProps } from '@src/components/Button/Button'
import { Button } from '@src/components/Button/Button'

export type ErrorModalBaseProps = {
  children?: ReactNode
  title?: string
  message?: string
  ctaText?: string
  onClick?: () => void
  ctaProps?: Omit<ButtonProps & { 'data-testid'?: string }, 'children'>
}

export const ErrorModalBase = (props: ErrorModalBaseProps) => {
  const { children, ctaText, message, onClick = noop, title, ctaProps } = props

  return (
    <Dialog
      transition
      open
      onClose={onClick}
      className="relative z-50 transition duration-300 ease-out data-[closed]:opacity-0"
    >
      <DialogBackdrop className="fixed inset-0 bg-bodi-black/90 duration-300 ease-out data-[closed]:opacity-0" />

      <div className="fixed inset-0 flex w-screen items-start justify-center overflow-y-auto p-4 pt-16 lg:pt-28">
        <DialogPanel className="relative w-full max-w-2xl rounded-2xl bg-bodi-white p-6 duration-300 ease-out data-[closed]:scale-95 data-[closed]:opacity-0">
          <button
            onClick={onClick}
            className="absolute right-0 top-0 m-4 flex size-8 cursor-pointer appearance-none items-center justify-center rounded-full text-bodi-gray-dark transition-colors hover:bg-bodi-gray-pearl"
          >
            <XMarkIcon className="size-6 stroke-[2px]" />
          </button>
          <div className="mb-4 inline-block rounded-full bg-red-50 p-2">
            <div className="rounded-full bg-red-100 p-2">
              <ExclamationCircleIcon className="size-6 text-red-600" />
            </div>
          </div>
          <DialogTitle className="text-heading-4 mb-1 font-bold">{title}</DialogTitle>
          <Description className="mb-8 text-bodi-cool-gray-500">{message}</Description>

          {children}

          <Button size="small" className="py-4" onClick={onClick} {...ctaProps}>
            <div className="font-bold">{ctaText}</div>
          </Button>
        </DialogPanel>
      </div>
    </Dialog>
  )
}
